<script setup lang="ts">
import type UiAccordion from '@afs/design-system/components/molecules/UiAccordion/UiAccordion.vue.d.ts'
import { UiAccordionVariant } from '@afs/design-system'
import type { SubProductPageData } from './+data'
import { useData } from 'vike-vue/useData'
import { useI18n } from 'vue-i18n'
import { navigate } from 'vike/client/router'
import { usePageContext } from 'vike-vue/usePageContext'
import { useFormatSeoContent } from '@/composables/useFormatSeoContent'
import { useGtmOnPageMounted, useGtmSimulatorClick } from '@/composables/useGtmTag'
import { useSimulation } from '@/composables/useSimulation'
import { onMounted, ref, watch } from 'vue'
import { openLegalNotice, scrollToLegalNotice } from '@/composables/useAccordion'

const legalNoticeAccordion = ref<InstanceType<typeof UiAccordion> | null>(null)
onMounted(() => openLegalNotice(legalNoticeAccordion.value))

const pageContext = usePageContext()
const data = useData<SubProductPageData>()
useFormatSeoContent(data.subProduct.seoTitle, data.subProduct?.hero.subtitle)
useGtmOnPageMounted('product')
const trackSubmit = useGtmSimulatorClick()

const { heroProps, offerTipsBlocProps, additionnalLegalProps } = useSimulation(data.subProduct)

const { t } = useI18n()

const breadcrumb = {
  ariaLabel: t('breadcrumb.ariaLabel'),
  items: [
    { text: t('page.accueil'), to: '/' },
    {
      text:
        data.subProduct?.product?.hub.title && data.subProduct.product.hub.title.length > 40
          ? `${data.subProduct.product.hub.title.slice(0, 40)}...`
          : data.subProduct?.product?.hub.title,
      to: `/${pageContext.routeParams.productHub}`,
    },
    {
      text:
        data.subProduct?.product?.title && data.subProduct.product.title.length > 40
          ? `${data.subProduct.product.title.slice(0, 40)}...`
          : data.subProduct?.product?.title,
      to: `/${pageContext.routeParams.productHub}/${pageContext.routeParams.product}`,
    },
    {
      text:
        data.subProduct?.breadcrumbTitle && data.subProduct.breadcrumbTitle.length > 40
          ? `${data.subProduct.breadcrumbTitle.slice(0, 40)}...`
          : data.subProduct?.breadcrumbTitle,
    },
  ],
}

const onSimulatorSubmit = (payload: string | number | null, event?: Event) => {
  if (typeof payload === 'string') return

  if (event && event.type === 'submit') {
    const submitEvent = event as SubmitEvent

    if (submitEvent.submitter?.innerText) {
      trackSubmit(submitEvent.submitter.innerText)
    }
  }

  const searchParams = new URLSearchParams()
  if (payload) searchParams.set('amount', payload?.toString())
  searchParams.set('offer', data.offerId ?? '')
  searchParams.set('subOffer', data.subOffer ?? '')
  navigate(`/simulateur?${searchParams}`)
}

const subProductSimulatordProps = data.subProduct?.hero.simulator
  ? {
      ...data.subProduct.hero.simulator,
      action: onSimulatorSubmit,
    }
  : undefined

const legalNotice = {
  id: 'sub-product-legal-notice',
  title: t('homepage.mentionLegal.title'),
}

// On force la mise à jour du contenu en cas de navigation entre les différentes pages sous-produits
watch(
  () => pageContext.routeParams.subProduct,
  async () => window.location.reload(),
)
</script>

<template>
  <div
    v-if="data.subProduct"
    class="SubProduct"
  >
    <organisms-ui-hero
      v-if="heroProps"
      class="Hero"
      v-bind="heroProps"
      :simulator="subProductSimulatordProps"
      :legal-notice-button-label="$t('subProduct.hero.legalNoticeButton')"
      @hero::on-legal-notice-click="scrollToLegalNotice(legalNoticeAccordion)"
    />
    <molecules-ui-breadcrumb
      v-bind="breadcrumb"
      class="SubProduct__breadcrumb"
    />
    <div class="container my-8">
      <section
        v-if="data.subProduct.blockText"
        class="grid mb-8"
      >
        <div class="SubProduct__subOffer color-blue-01">
          <h2 class="heading-2">{{ data.subProduct.blockText.title }}</h2>
          <h3 class="paragraph-2 mb-4 color-red-01">{{ data.subProduct.blockText.subtitle }}</h3>
          <p class="paragraph-2 mb-2 longtext">
            {{ data.subProduct.blockText.chapo }}
          </p>
          <molecules-ui-rich-text
            v-if="data.subProduct.blockText.content"
            :content="data.subProduct.blockText.content"
          />
        </div>
      </section>
      <section class="SubProduct__insurance grid mb-8">
        <molecules-ui-focus
          v-bind="data.subProduct.insuranceBlock"
          illustration="shield-blue"
        />
        <div
          v-if="data.subProduct.advantageBlock"
          class="SubProduct__advantages"
        >
          <h2 class="SubProduct__advantages__title heading-2 color-blue-01">
            {{ data.subProduct.advantageBlock.title }}
          </h2>
          <ul class="SubProduct__advantages__list g-3">
            <template
              v-for="(item, index) in data.subProduct.advantageBlock.items"
              :key="`SubProduct-advantages-advantage-${index}`"
            >
              <li v-if="item.text">
                <molecules-ui-list-item :text="item.text" />
              </li>
            </template>
          </ul>
        </div>
      </section>
      <section
        v-if="data.subProduct.adviceBlock"
        class="grid mb-8"
      >
        <molecules-ui-highlight
          class="SubProduct__highlight"
          v-bind="data.subProduct.adviceBlock"
        />
      </section>
      <section class="SubProduct__offer grid">
        <molecules-ui-offer-tips
          v-if="offerTipsBlocProps"
          v-bind="offerTipsBlocProps"
        />
      </section>
    </div>
    <organisms-ui-steps
      v-if="data.subProduct.stepsBlock"
      :title="data.subProduct.stepsBlock.title"
      :steps="data.subProduct.stepsBlock.steps"
      :cta="data.subProduct.stepsBlock.cta"
    />
    <div class="container my-8">
      <section class="grid">
        <div class="SubProduct__faq">
          <h2 class="SubProduct__faq__title heading-2 color-blue-01">{{ data.subProduct.faqBlock.title }}</h2>
          <molecules-ui-accordion
            :items="data.subProduct.faqList"
            :variant="UiAccordionVariant.SECONDARY"
            name="suboffer-page-faq"
          >
            <template
              v-for="question in data.subProduct.faqList"
              :key="question.id"
              #[question.id]
            >
              <h3
                v-if="question.subtitle"
                class="mb-2 heading-4"
              >
                {{ question.subtitle }}
              </h3>
              <molecules-ui-rich-text :content="question.content" />
            </template>
          </molecules-ui-accordion>
        </div>
      </section>
    </div>
    <organisms-ui-related-articles
      v-if="data.articles"
      :title="data.subProduct.blockArticles.title"
      :articles="data.articles"
    />
    <div class="container my-8">
      <section class="grid">
        <molecules-ui-accordion
          ref="legalNoticeAccordion"
          class="SubProduct__legalNotice mt-4"
          :items="[legalNotice]"
        >
          <template
            v-for="item in [legalNotice]"
            :key="item.id"
            #[item.id]
          >
            <molecules-ui-rich-text
              v-if="additionnalLegalProps"
              class="mt-3"
              :content="additionnalLegalProps"
            />
            <molecules-ui-rich-text
              v-if="data.subProduct.legalNoticeBlock.legalNotice"
              class="mt-3"
              :content="data.subProduct.legalNoticeBlock.legalNotice"
            />
          </template>
        </molecules-ui-accordion>
      </section>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use 'sass:map';
@use 'config/spaces';
@use 'utilities/mq';

.SubProduct__breadcrumb {
  position: absolute;
  top: 0;
}

.SubProduct__subOffer,
.SubProduct__highlight,
.SubProduct__faq {
  grid-column: span 4;

  @include mq.media('>=medium') {
    grid-column: 2 / span 10;
  }
}

.SubProduct__insurance {
  align-items: center;

  @include mq.media('<medium') {
    row-gap: map.get(spaces.$sizes, '7');
  }

  .Focus {
    grid-column: span 4;

    @include mq.media('>=medium') {
      grid-column: 2 / span 5;
    }
  }
}

.SubProduct__advantages {
  grid-column: span 4;

  @include mq.media('>=medium') {
    grid-column: span 5;
  }
}

.SubProduct__advantages__title {
  margin-bottom: map.get(spaces.$sizes, '5');

  @include mq.media('>=medium') {
    margin-bottom: map.get(spaces.$sizes, '6');
  }
}

.SubProduct__advantages__list {
  display: flex;
  flex-direction: column;
}

.SubProduct__offer {
  .OfferTips {
    grid-column: span 4;

    @include mq.media('>=medium') {
      grid-column: 2 / span 10;
    }
  }
}

.SubProduct__faq__title {
  margin-bottom: map.get(spaces.$sizes, '6');
  text-align: center;
}

.SubProduct__rateExample {
  @include mq.media('<medium') {
    row-gap: map.get(spaces.$sizes, '7');
  }
}

.SubProduct__rateExample__left {
  grid-column: span 4;

  @include mq.media('>=medium') {
    grid-column: 1 / span 6;
  }
}

.SubProduct__rateExample__right {
  grid-column: span 4;
  display: flex;
  flex-direction: column;
  gap: map.get(spaces.$sizes, '5');
  align-items: stretch;

  @include mq.media('>=medium') {
    grid-column: span 6;
    gap: map.get(spaces.$sizes, '6');
  }
}

.SubProduct__rateExample__right__title {
  align-self: center;
}

.SubProduct__legalNotice {
  --scroll-offset: 50px;

  grid-column: span 4;

  @include mq.media('>=medium') {
    --scroll-offset: 200px;

    grid-column: 2 / span 10;
  }

  &:deep(.Accordion__item) {
    scroll-margin: calc(var(--header-height) + var(--scroll-offset));
  }
}
</style>
